import { format } from 'date-fns';
import { FC, useState, useEffect, Fragment } from 'react';

import { useComboProgressContext } from '../../hooks/contexts/comboProgress.context';
import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import useFormattedDateTimeZone from '../../hooks/useFormattedDateTimeZone';
import { ComboDate } from '../../types/ticket';
import ComboLayout from '../Layout/combo';
import Popover from '../Popover';
import * as Styles from './combo-tickets-list.styles';

export type ComboTicketsSelected = {
  [key : number] : {
    groupId: number;
    ticketId: number;
  }
}

type FormattedDateTimeCards = {
  title : string;
} & ComboDate;

const ComboTicketsList: FC = () => {
  const [localSelectedTickets, setLocalSelectedTickets] = useState<ComboTicketsSelected[]>([]);
  const [localLoading, setLocalLoading] = useState<boolean>(true);

  const { comboData } = useComboProgressContext();
  const { formatDate } = useFormattedDateTimeZone();
  const { event } = useEventContext();
  const {
    supportedLocales,
    translate,
    currentLocale : { locale : l },
  } = useI18nContext();

  const locale = supportedLocales({ lang : l });

  const formattedDateTimeCards: FormattedDateTimeCards[] = comboData.comboDates.map((comboDate) => {
    const curDateTime = formatDate(comboDate.datetime, event?.timezone ?? 'America/Sao_Paulo');

    const hourFormat = locale.code === 'en-US' ? 'hh\':\'mm a' : 'HH\':\'mm';
    const hour = format(curDateTime, hourFormat, { locale }).toLowerCase();
    const week = format(curDateTime, 'eee', { locale }).substring(0, 3);
    const day = format(curDateTime, 'dd', { locale });
    const month = format(curDateTime, 'MMMM', { locale }).substring(0, 3);
    const year = format(curDateTime, 'yyyy', { locale });

    const expectedDate = locale.code === 'en-US' ? (
      `${month} ${day} ${year}`.toUpperCase()
    ) : `${day} ${month} ${year}`.toUpperCase();

    const expectedWeekAndHour = `${week} • ${hour}`;

    return {
      title : `${expectedDate}, ${expectedWeekAndHour}`,
      ...comboDate,
    };
  });

  const handleIsSelectedTicket = (
    groupId: number,
    ticketId: number,
    sessionId: number,
  ) => {
    const curSessionObj = localSelectedTickets.find((curTicket) => curTicket[sessionId]) as ComboTicketsSelected;
    const findedTicketId = curSessionObj[sessionId].ticketId;
    const findedGroupId = curSessionObj[sessionId].groupId;

    return findedTicketId === ticketId && findedGroupId === groupId;
  };

  const handleSelectedTickets = (groupId: number, ticketId: number, sessionId: number) => {
    const expectedLocalSelectedTickets = localSelectedTickets.map((curTicket) => {
      if (curTicket[sessionId]) return { [sessionId] : { groupId, ticketId } };
      return curTicket;
    });

    setLocalSelectedTickets(expectedLocalSelectedTickets);
  };

  useEffect(() => {
    const expectedLocalSelectedTickets = comboData.comboDates.map((comboDate) => {
      let groupId = null;
      let ticketId = null;
      let maxPrice = -Infinity;

      for (const group of comboDate.groups) {
        for (const ticket of group.tickets) {
          if (ticket.price > maxPrice) {
            maxPrice = ticket.price;
            groupId = group.ticketTypeId;
            ticketId = ticket.id;
          }
        }
      }

      return { [comboDate.id] : { groupId, ticketId } };
    }) as ComboTicketsSelected[];

    setLocalSelectedTickets(expectedLocalSelectedTickets);
    setLocalLoading(false);
  }, []);

  if (localLoading) return null;

  return (
    <ComboLayout
      curComponent="tickets"
      selectedTickets={localSelectedTickets}
    >
      <Fragment>
        { formattedDateTimeCards.map((comboDate) => (
          <Styles.ComboListContainer key={comboDate.title}>
            <Styles.ComboListTitle>
              {comboDate.title}
            </Styles.ComboListTitle>
            <Styles.ListOfCombos>
              {comboDate.groups.map((group) => group.tickets.map((ticket) => (
                <Styles.ComboListItem
                  key={ticket.id}
                  $selected={handleIsSelectedTicket(
                    group.ticketTypeId,
                    ticket.id,
                    comboDate.id,
                  )}
                >
                  <label
                    htmlFor={`selection-combo-ticket-${ticket.id}`}
                  >
                    <input
                      onChange={() => handleSelectedTickets(group.ticketTypeId, ticket.id, comboDate.id)}
                      type="radio"
                      name={`selection-combo-ticket-${group.ticketTypeId}`}
                      id={`selection-combo-ticket-${ticket.id}`}
                      checked={handleIsSelectedTicket(
                        group.ticketTypeId,
                        ticket.id,
                        comboDate.id,
                      )}
                    />
                    <Popover
                      render={(
                        comboDate.groups.length === 1
                        && group.tickets.length === 1
                      )}
                      id={`combo-tooltip-${ticket.id}`}
                      text={translate(['COMBO', 'COMBO_TICKET_LIST', 'POPOVER'])}
                    >
                      {ticket.name}
                    </Popover>
                  </label>
                </Styles.ComboListItem>
              )))}
            </Styles.ListOfCombos>
          </Styles.ComboListContainer>
        ))}
      </Fragment>
    </ComboLayout>
  );
};

export default ComboTicketsList;
